import React, { Suspense, lazy, useCallback, useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookiesProvider } from 'react-cookie';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useCookies } from 'react-cookie';



import './App.css';
import PaperWrapper from "./components/page_wrapper";

/*
 * Pages are loaded on demand.
 *
 * Every page used to be imported eagerly, so one bundle held the whole
 * application - MUI, the chart library, drag-and-drop, every screen - and the
 * browser had to download, parse and run all of it before the login form could
 * appear. In development that was a ~6.9 MB bundle for a page with two text
 * fields on it.
 *
 * React.lazy splits each route into its own chunk, fetched when somebody
 * actually navigates there. Login and Home stay eager: they are the first thing
 * anybody sees, and making them wait on a second round trip would trade one
 * delay for another.
 */
import LoginPage from './pages/login'
import HomePage from "./pages/homepage";

const AddInventory = lazy(() => import('./pages/inventory')
    .then((m) => ({ default: m.AddInventory })));
const ViewInventory = lazy(() => import('./pages/inventory')
    .then((m) => ({ default: m.ViewInventory })));
const ViewInventoryDetails = lazy(() => import('./pages/inventory')
    .then((m) => ({ default: m.ViewInventoryDetails })));
const AddPreparation = lazy(() => import('./pages/inventory')
    .then((m) => ({ default: m.AddPreparation })));
const AddWastage = lazy(() => import('./pages/inventory')
    .then((m) => ({ default: m.AddWastage })));
const AddBill = lazy(() => import('./pages/Bills')
    .then((m) => ({ default: m.AddBill })));
const ViewBillsList = lazy(() => import('./pages/Bills')
    .then((m) => ({ default: m.ViewBillsList })));
const ViewBillDetails = lazy(() => import('./pages/Bills')
    .then((m) => ({ default: m.ViewBillDetails })));
const ViewOrders = lazy(() => import('./pages/Orders')
    .then((m) => ({ default: m.ViewOrders })));
// The heaviest of the lot: it pulls in @mui/x-charts, which nothing else uses.
const AnalyticsDashboard = lazy(() => import('./pages/analytics'));
const FixedCosts = lazy(() => import('./pages/analytics/FixedCosts'));
const BusinessSettings = lazy(() => import('./pages/analytics/Settings'));
const BillItemNames = lazy(() => import('./pages/inventory/BillItemNames'));
const StockVariance = lazy(() => import('./pages/inventory/Variance'));
const MenuPage = lazy(() => import('./pages/analytics/Menu'));
const StaffList = lazy(() => import('./pages/staff'));
const StaffDetail = lazy(() => import('./pages/staff/StaffDetail'));
const Payroll = lazy(() => import('./pages/staff/Payroll'));
const PayslipDetail = lazy(() => import('./pages/staff/PayslipDetail'));
const Approvals = lazy(() => import('./pages/staff/Approvals'));
const MyStaffPage = lazy(() => import('./pages/staff/MyStaffPage'));
const RecipesPage = lazy(() => import('./pages/staff/Recipes'));



/**
 * Shared theme options.
 *
 * This app is used mostly on a phone, standing up, often one-handed in a
 * kitchen - so the defaults below raise every interactive element to a
 * comfortable tap size and stop small text getting smaller on small screens.
 * Setting it here fixes it everywhere at once rather than per component.
 */
const TOUCH_TARGET = 44;

const sharedOptions = {
  shape: { borderRadius: 10 },
  typography: {
    // MUI's default caption (0.75rem = 12px) is the floor, not a starting point.
    caption: { fontSize: '0.78rem' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { minHeight: TOUCH_TARGET, paddingLeft: 14, paddingRight: 14 },
        // `size="small"` is used for secondary actions; keep it visually
        // smaller but never below a tappable height.
        sizeSmall: { minHeight: TOUCH_TARGET, paddingTop: 4, paddingBottom: 4 },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET } },
    },
    MuiToggleButton: {
      styleOverrides: { root: { minHeight: TOUCH_TARGET } },
    },
    MuiSelect: {
      styleOverrides: { select: { minHeight: `${TOUCH_TARGET - 8}px`, display: 'flex', alignItems: 'center' } },
    },
    MuiOutlinedInput: {
      styleOverrides: { input: { minHeight: `${TOUCH_TARGET - 20}px` } },
    },
    MuiTab: {
      styleOverrides: { root: { minHeight: TOUCH_TARGET, textTransform: 'none' } },
    },
    // Tables are the one place horizontal scrolling is correct rather than a
    // bug: the alternative is truncating figures.
    MuiTableContainer: {
      styleOverrides: { root: { maxWidth: '100%', overflowX: 'auto' } },
    },
    // Deliberately NOT `whiteSpace: nowrap` on cells. It keeps numbers tidy but
    // stops long text wrapping, which pushed tables past the card that clips
    // them - a cut-off value is worse than a wrapped one. Numeric columns opt
    // in individually where alignment matters.
    MuiTable: {
      styleOverrides: { root: { tableLayout: 'auto' } },
    },
  },
};

const darkTheme = createTheme({
  ...sharedOptions,
  palette: { mode: 'dark' },
});

const lightTheme = createTheme({
  ...sharedOptions,
  palette: { mode: 'light' },
});


function App() {
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [cookies, setCookies] = useCookies(['divash-admin-theme']);

  useEffect(() => {
    if (!cookies['divash-admin-theme']) {
      setCookies('divash-admin-theme', "light", { path: '/' });
    }
  }, [cookies, setCookies]);

  const theme = useMemo(
    () => cookies['divash-admin-theme'] === 'light' ? lightTheme : darkTheme,
    [cookies['divash-admin-theme']]
  );

  // Both memoised with no dependencies, because both only call setState, which
  // React already keeps stable.
  //
  // They are passed down to every page, and the performance page puts
  // showErrorMessage in the dependency array of the callback that loads the
  // overview. Rebuilt on each render, it made that callback new on each render
  // too - so the 7.7-second, 63-query overview request re-fired up to three
  // times per upload, for no reason anybody could see.
  const showErrorMessage = useCallback((message) => {
    setSnackBarMessage(message);
    setAlertSeverity("error");
    setShowSnackBar(true);
  }, []);

  const showSuccessMessage = useCallback((message) => {
    setSnackBarMessage(message);
    setAlertSeverity("success");
    setShowSnackBar(true);
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setShowSnackBar(false);
  };

  return (
    <ThemeProvider theme={theme} >
      <CssBaseline />
      <BrowserRouter>
        <PaperWrapper theme={cookies['divash-admin-theme']} changeTheme={(theme) => {
          setCookies('divash-admin-theme', theme);
        }}>
          {/* Anchored to the top. At the bottom it sat directly on top of the
              action buttons of a full-screen dialog on a phone - a confirmation
              of the last action blocking the button for the next one - and for
              thirty seconds, long after it had stopped being about anything the
              user was still doing. */}
          <Snackbar
            open={showSnackBar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={10000}
            onClose={handleSnackbarClose}
          >
            <Alert
              onClose={handleSnackbarClose}
              variant="filled"
              severity={alertSeverity}
            >
              {snackBarMessage}
            </Alert>
          </Snackbar>
          <CookiesProvider defaultSetOptions={{ path: '/' }}>
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            }>
            <Routes>
              <Route path="/" element={<LoginPage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              {/* Removed redundant index route */}
              <Route path="/home" element={<HomePage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/status" element={<AddInventory showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/preparation" element={<AddPreparation showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/view" element={<ViewInventory showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/view/details/*" element={<ViewInventoryDetails showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/wastage/" element={<AddWastage route="wastage" showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/usage/" element={<AddWastage route="usage" showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/bill/add" element={<AddBill showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/bill/view" element={<ViewBillsList showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/bill/view/*" element={<ViewBillDetails showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/orders/view" element={<ViewOrders showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/analytics" element={<AnalyticsDashboard showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/analytics/fixed-costs" element={<FixedCosts showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/analytics/settings" element={<BusinessSettings showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/bill-names" element={<BillItemNames showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/variance" element={<StockVariance showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/menu" element={<MenuPage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              {/* Everyone's own leave and payslips. The only staff page a
                  non-owner account can open. */}
              <Route path="/me" element={<MyStaffPage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/recipes" element={<RecipesPage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/staff" element={<StaffList showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/staff/payroll" element={<Payroll showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/staff/requests" element={<Approvals showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/staff/payslip/:payslipId" element={<PayslipDetail showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/staff/:staffId" element={<StaffDetail showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route
                path="*"
                element={
                  <>TODO</>
                }
              />
            </Routes>
            </Suspense>
          </CookiesProvider>
        </PaperWrapper>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
